import React, { useState, useEffect } from 'react';
import { View, Text, Pressable, Modal, StyleSheet } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import SleekTimePicker from '../../../../components/time/SleekTimePicker';

interface TimeRangeEditModalProps {
    visible: boolean;
    onClose: () => void;
    currentStartTime: string;
    currentEndTime: string;
    onSave: (startTime: string, endTime: string) => void;
    sessionTitle?: string;
}

const TimeRangeEditModal: React.FC<TimeRangeEditModalProps> = ({
    visible,
    onClose,
    currentStartTime,
    currentEndTime,
    onSave,
    sessionTitle = "Edit Time Range"
}) => {
    const [startTime, setStartTime] = useState(currentStartTime || '');
    const [endTime, setEndTime] = useState(currentEndTime || '');
    const [error, setError] = useState('');

    // Update state when modal opens or current values change
    useEffect(() => {
        if (visible) {
            setStartTime(currentStartTime || '');
            setEndTime(currentEndTime || '');
            setError('');
        }
    }, [visible, currentStartTime, currentEndTime]);

    const validateTime = (time: string) => {
        if (!time.trim()) return false;
        // Basic time format validation (accepts various formats)
        const timeRegex = /^(0?[1-9]|1[0-2]):[0-5][0-9]\s?(AM|PM|am|pm)$/;
        return timeRegex.test(time.trim());
    };

    const validateTimeRange = (start: string, end: string) => {
        if (!start.trim() || !end.trim()) {
            return 'Both start and end times are required';
        }
        if (!validateTime(start)) {
            return 'Start time must be in valid format (e.g., 9:00 AM)';
        }
        if (!validateTime(end)) {
            return 'End time must be in valid format (e.g., 5:00 PM)';
        }
        
        // Convert to 24-hour format for comparison
        const convertTo24Hour = (time12h: string) => {
            const [time, modifier] = time12h.split(' ');
            let [hours, minutes] = time.split(':');
            if (hours === '12') {
                hours = '00';
            }
            if (modifier.toUpperCase() === 'PM') {
                hours = (parseInt(hours, 10) + 12).toString();
            }
            // Zero-pad hours for proper string comparison
            hours = hours.padStart(2, '0');
            return `${hours}:${minutes}`;
        };

        try {
            const startTime24 = convertTo24Hour(start.trim());
            const endTime24 = convertTo24Hour(end.trim());
            
            if (startTime24 >= endTime24) {
                return 'End time must be after start time';
            }
        } catch (e) {
            return 'Invalid time format';
        }
        
        return '';
    };



    const isValid = !error && startTime.trim() !== '' && endTime.trim() !== '';

    const handleSave = () => {
        const validationError = validateTimeRange(startTime, endTime);
        if (validationError) {
            setError(validationError);
            return;
        }
        onSave(startTime.trim(), endTime.trim());
        onClose();
    };

    const handleCancel = () => {
        setStartTime(currentStartTime || '');
        setEndTime(currentEndTime || '');
        onClose();
    };

    return (
        <Modal
            visible={visible}
            transparent={true}
            animationType="fade"
            onRequestClose={onClose}
        >
            <View style={styles.overlay}>
                <View style={styles.modalContainer}>
                    {/* Header */}
                    <View style={styles.header}>
                        <Text style={styles.title}>{sessionTitle}</Text>
                        <Pressable onPress={onClose} style={styles.closeButton}>
                            <MaterialCommunityIcons name="close" size={20} color="#666" />
                        </Pressable>
                    </View>

                    {/* Content */}
                    <View style={styles.content}>
                        <SleekTimePicker
                            startTime={currentStartTime}
                            endTime={currentEndTime}
                            onTimeChange={(newStartTime, newEndTime) => {
                                setStartTime(newStartTime);
                                setEndTime(newEndTime);
                                setError(validateTimeRange(newStartTime, newEndTime));
                            }}
                        />
                        
                        {error && (
                            <Text style={styles.errorText}>{error}</Text>
                        )}
                    </View>

                    {/* Footer */}
                    <View style={styles.footer}>
                        <Pressable onPress={handleCancel} style={[styles.button, styles.cancelButton]}>
                            <Text style={styles.cancelButtonText}>Cancel</Text>
                        </Pressable>
                        <Pressable 
                            onPress={handleSave} 
                            style={[styles.button, isValid ? styles.saveButton : styles.saveButtonDisabled]}
                            disabled={!isValid}
                        >
                            <Text style={[styles.saveButtonText, !isValid && styles.saveButtonTextDisabled]}>Save</Text>
                        </Pressable>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalContainer: {
        backgroundColor: 'white',
        borderRadius: 12,
        width: '100%',
        maxWidth: 550,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25,
        shadowRadius: 12,
        elevation: 8,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#e9ecef',
    },
    title: {
        fontSize: 18,
        fontWeight: '600',
        color: '#333',
    },
    closeButton: {
        padding: 4,
    },
    content: {
        padding: 20,
    },
    errorText: {
        fontSize: 12,
        color: '#dc3545',
        marginTop: 12,
        textAlign: 'center',
        fontWeight: '500',
    },
    footer: {
        flexDirection: 'row',
        padding: 20,
        paddingTop: 0,
        gap: 12,
    },
    button: {
        flex: 1,
        paddingVertical: 12,
        borderRadius: 8,
        alignItems: 'center',
    },
    cancelButton: {
        backgroundColor: '#f8f9fa',
        borderWidth: 1,
        borderColor: '#e9ecef',
    },
    cancelButtonText: {
        fontSize: 16,
        fontWeight: '500',
        color: '#666',
    },
    saveButton: {
        backgroundColor: '#007bff',
    },
    saveButtonDisabled: {
        backgroundColor: '#cccccc',
    },
    saveButtonText: {
        fontSize: 16,
        fontWeight: '500',
        color: 'white',
    },
    saveButtonTextDisabled: {
        color: '#666666',
    },
});

export default TimeRangeEditModal; 